/**
 * The pieces every salary PIN step that ends in a NEW PIN shares (ADR 0041): the check, the two boxes and
 * the button row. Their own file so both forgot flows (`SalaryPinForms`, `SalaryPinNewPhoneFlow`) can use
 * them without importing each other.
 */

import type { ReactNode } from 'react';
import { salaryPinSchema, SALARY_PIN_MAX_LENGTH, SALARY_PIN_MIN_LENGTH } from '@hrms/shared';
import { SalaryPinField } from './SalaryPinField.js';

/** A problem with a NEW PIN, in the words the API would use, or null. */
export function newPinProblem(pin: string, confirm: string): string | null {
  const parsed = salaryPinSchema.safeParse(pin);
  if (!parsed.success) return parsed.error.issues[0]?.message ?? 'Use 4 to 6 digits';
  if (pin !== confirm) return 'The two PINs do not match';
  return null;
}

export function PinActions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center justify-end gap-2 pt-1">{children}</div>;
}

/** A new PIN and its confirmation — the shape create, change and reset all end in. */
export function NewPinFields({
  pin,
  confirm,
  onPin,
  onConfirm,
  problem,
  disabled,
  autoFocus = true,
}: {
  pin: string;
  confirm: string;
  onPin: (value: string) => void;
  onConfirm: (value: string) => void;
  problem: string | null;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <>
      <SalaryPinField
        label="New PIN"
        name="newPin"
        value={pin}
        onChange={onPin}
        hint={`${SALARY_PIN_MIN_LENGTH} to ${SALARY_PIN_MAX_LENGTH} digits. Avoid repeated or sequential digits.`}
        autoFocus={autoFocus}
        disabled={disabled}
      />
      <SalaryPinField
        label="Confirm new PIN"
        name="confirmPin"
        value={confirm}
        onChange={onConfirm}
        error={problem}
        disabled={disabled}
      />
    </>
  );
}
